import React, { type FC, type SyntheticEvent, type PropsWithChildren } from 'react';

import { mqMin } from '@lucid/theme';

import type { Lucid } from '../../types/types';
import Box from '../../components/box/box';
import Link from '../../components/link/link';

export interface NavMenuIconOptions {
  /** Checks if the navbar is on the top most nav. */
  isTopNav?: boolean;
}

export interface NavMenuIconProps extends NavMenuIconOptions {
  /** Checks if the navigation menu is open. */
  isMenuOpen: boolean;
  /** The onClick function to open the dropdown menu. */
  onClick: (e: SyntheticEvent) => void;
}

const NavMenuIcon: FC<PropsWithChildren<NavMenuIconProps>> = ({ isMenuOpen, isTopNav, onClick, ...rest }) => (
  <Link
    {...rest}
    asButton
    aria-controls={`${isTopNav ? 'top-' : ''}navMobileIcon`}
    aria-expanded={isMenuOpen || undefined}
    aria-haspopup="true"
    css={{
      label: 'navMenuIcon',
      alignSelf: 'center',
      [mqMin.tabletLandscape]: {
        display: 'none',
        visibility: 'hidden',
      },
    }}
    onClick={onClick}
    variant="navLink"
  >
    <Box
      css={(theme: Lucid.Theme) => [
        {
          label: 'navIcon',
          width: '40px',
          height: '35px',
          position: 'relative',
          transform: 'rotate(0deg)',
          transition: '0.5s ease-in-out',
          cursor: 'pointer',
          '& > span': {
            display: 'block',
            position: 'absolute',
            height: '7px',
            width: '50%',
            background: theme.colors.primary,
            opacity: 1,
            transform: 'rotate(0deg)',
            transition: '0.25s ease-in-out',
            pointerEvents: 'none',
          },
          '& > span:nth-of-type(even)': {
            left: '50%',
            borderRadius: '0 7px 7px 0',
          },
          '& > span:nth-of-type(odd)': {
            left: 0,
            borderRadius: '7px 0 0 7px',
          },
          '& > span:nth-of-type(1), & > span:nth-of-type(2)': {
            top: 0,
          },
          '& > span:nth-of-type(3), & > span:nth-of-type(4)': {
            top: '14px',
          },
          '& > span:nth-of-type(5), & > span:nth-of-type(6)': {
            top: '28px',
          },
        },
        isMenuOpen && {
          '& > span:nth-of-type(1), & > span:nth-of-type(6)': {
            transform: 'rotate(45deg)',
          },
          '& > span:nth-of-type(2), & > span:nth-of-type(5)': {
            transform: 'rotate(-45deg)',
          },
          '& > span:nth-of-type(1)': {
            top: '6px',
            left: '1px',
          },
          '& > span:nth-of-type(2)': {
            top: '6px',
            left: 'calc(50% - 6px)',
          },
          '& > span:nth-of-type(3)': {
            left: '-50%',
            opacity: 0,
          },
          '& > span:nth-of-type(4)': {
            left: '100%',
            opacity: 0,
          },
          '& > span:nth-of-type(5)': {
            top: '20px',
            left: '1px',
          },
          '& > span:nth-of-type(6)': {
            top: '20px',
            left: 'calc(50% - 6px)',
          },
        },
        isTopNav && {
          '& > span': {
            background: theme.colors.coolGrey[100],
          },
        },
      ]}
      id={`${isTopNav ? 'top-' : ''}navMobileIcon`}
    >
      <span />
      <span />
      <span />
      <span />
      <span />
      <span />
    </Box>
  </Link>
);

export default NavMenuIcon;
